/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Link, NoImage } from '@carbon/react/icons';
import { Button, Loading, Modal, TextInput } from 'carbon-components-react';
import React, { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StyleTab as messages } from '../messages';

const sizes = {
  min: 64,
  max: 100
};

const ids = {
  AVATAR_BUTTON: 'avatar-button',
  AVATAR_MODAL: 'avatar-modal'
};

interface AvatarFieldProps {
  /**
   *
   */
  id: string;
  /**
   *
   */
  defaultValue?: string;
  /**
   *
   */
  updateBotAvatarURL: (url: string) => void;
  /**
   *
   */
  readOnly: boolean;
}
export const AvatarField = ({
  id,
  defaultValue,
  updateBotAvatarURL,
  readOnly
}: AvatarFieldProps) => {
  const { formatMessage: t } = useIntl();

  // state values
  const [avatarURL, setAvatarURL] = useState<string>(defaultValue ?? '');
  const [avatarURLError, setAvatarURLError] = useState<
    JSX.Element | string | null
  >(null);
  const [avatarPreviewLoading, setAvatarPreviewLoading] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setAvatarURL(defaultValue ?? '');
  }, [defaultValue]);

  const setRefocus = (refocusingId: string) => {
    setTimeout(() => {
      const focusElt = document.getElementById(refocusingId);
      if (focusElt) {
        return focusElt.focus();
      } else {
        return null;
      }
    }, 100);
  };

  const onSubmit = () => {
    const value = (document.getElementById(id) as HTMLTextAreaElement).value;
    if (value !== avatarURL) {
      setAvatarURL(value);
      setAvatarURLError(null);
      setAvatarPreviewLoading(!!value);
      setIsModalOpen(!!value);
      if (!value) {
        updateBotAvatarURL('');
      }
    } else {
      setAvatarPreviewLoading(false);
      setIsModalOpen(false);
    }
    setRefocus(ids.AVATAR_BUTTON);
  };

  const onClose = () => {
    setIsModalOpen(false);
    setAvatarURL(defaultValue ?? '');
    setRefocus(ids.AVATAR_BUTTON);
  };

  const openAvatarModal = () => {
    setIsModalOpen(true);
  };

  const isValidSquare = (width, height) => {
    return width === height && width <= sizes.max && width >= sizes.min;
  };

  const handleImageLoad = ({ target: img }) => {
    const isValid = isValidSquare(img.naturalWidth, img.naturalHeight);
    setAvatarURLError(
      !isValid ? (
        <FormattedMessage
          {...messages.avatarImageSizeError}
          values={{
            maxSize: sizes.max,
            minSize: sizes.min
          }}
        />
      ) : null
    );
    setAvatarPreviewLoading(false);
    setIsModalOpen(!isValid);
    if (isValid && avatarURL !== defaultValue) {
      updateBotAvatarURL(avatarURL);
    }
  };

  const handleImageError = _event => {
    setAvatarURLError(t(messages.avatarImageError));
    setAvatarPreviewLoading(false);
  };

  return (
    <Fragment>
      <div className="Style-tab-avatar--field-container">
        <div className="Style-tab-avatar--preview">
          {avatarPreviewLoading ? (
            <Loading
              className="Style-tab-avatar--loading"
              withOverlay={false}
              small
            />
          ) : !avatarURL || !!avatarURLError ? (
            <NoImage size={32} />
          ) : null}
          {!!avatarURL && (
            <img
              className="Style-tab-avatar--preview-img"
              style={
                avatarPreviewLoading || !!avatarURLError
                  ? { display: 'none' }
                  : {}
              }
              src={avatarURL}
              alt={t(messages.avatarModalHeader)}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
        </div>
        <Button
          disabled={readOnly}
          className="Style-tab-avatar--button"
          id={ids.AVATAR_BUTTON}
          kind="ghost"
          size="sm"
          renderIcon={Link}
          onClick={openAvatarModal}>
          {Boolean(avatarURL) ? (
            <FormattedMessage {...messages.avatarChange} />
          ) : (
            <FormattedMessage {...messages.avatarAdd} />
          )}
        </Button>
      </div>
      {isModalOpen && (
        <Modal
          id={ids.AVATAR_MODAL}
          modalHeading={<FormattedMessage {...messages.avatarModalHeader} />}
          primaryButtonText={<FormattedMessage {...messages.avatarModalSave} />}
          secondaryButtonText={
            <FormattedMessage {...messages.avatarModalClose} />
          }
          open={isModalOpen}
          onRequestClose={onClose}
          onRequestSubmit={onSubmit}
          selectorPrimaryFocus=".bx--text-input"
          primaryButtonDisabled={!!avatarURLError || avatarPreviewLoading}
          shouldSubmitOnEnter={!avatarURLError && !avatarPreviewLoading}>
          <p className="Style-tab-avatar--modal-description">
            <FormattedMessage {...messages.avatarDescription} />
            <br />
            <FormattedMessage
              values={{
                maxSize: sizes.max,
                minSize: sizes.min
              }}
              {...messages.avatarImageSizeDescription}
            />
          </p>
          <div className="Style-tab-avatar--modal-input-container">
            <TextInput
              id={id}
              className="TabContent__field Style-tab-avatar--field"
              labelText={<FormattedMessage {...messages.avatarPlaceholder} />}
              placeholder={t(messages.avatarPlaceholder)}
              hideLabel={true}
              disabled={avatarPreviewLoading}
              defaultValue={defaultValue}
              autoComplete="off"
              onChange={() => setAvatarURLError(null)}
              invalid={!avatarPreviewLoading && !!avatarURLError}
              invalidText={avatarURLError}
            />
            {avatarPreviewLoading ? (
              <Loading
                className="Style-tab-avatar--loading"
                withOverlay={false}
                small
              />
            ) : (
              <Link size={16} />
            )}
          </div>
        </Modal>
      )}
    </Fragment>
  );
};
